/**
 * ClubCheckoutForm.js — Club signup payment step (kind "club-checkout-form").
 *
 * Common Commerce7 club feature. Address/ship/pickup collection for club
 * checkout reuses the exact same "checkout-form"/"checkout-address-options"
 * components the cart checkout uses (see club_c7.js) — one shipping/pickup
 * UI across cart orders, club orders, or any future membership flow — so
 * this component only renders the payment step.
 *
 * "payment" — saved-card selection, PLUS an optional "+ Add new card" for
 *   tenants that configure a Stripe publishable key (commerce7.stripe_publishable_key
 *   — see core/configAccessors.js's getCommerce7Config, features/optional/stripe_c7.js).
 *   Commerce7's add-card-to-customer endpoint (needed so a brand-new card can
 *   be saved and charged by id — club payment only ever charges via
 *   customerCreditCardId, never a raw card number) requires a real tokenized
 *   card, which cart checkout's own new-card path doesn't produce (it hits a
 *   different Commerce7 endpoint that accepts raw card fields directly).
 *   Stripe.js closes that gap for club/membership payment ONLY — cart
 *   checkout is untouched and still has no tokenizer. Tenants with no Stripe
 *   key configured keep the exact prior behavior: saved cards only, and a
 *   "no saved payment method" dead end if there are none.
 *   Same visual row style as the cart checkout's own saved-card list
 *   (CheckoutForm.js's _loadSavedCards: bold brand/Default line, masked
 *   number + expiry sub-line).
 *
 * message.data: { type: "payment", savedCards: [...] }
 *
 * EVENTS OUT: ws-club-payment-submit {cardId} | ws-club-payment-submit {card}
 *   (card = a Stripe-tokenized new card: tokenOnFile/maskedCardNumber/
 *   cardBrand/cardHolderName/expiryMo/expiryYr — see club_c7.js's
 *   handlePaymentSubmit)
 */

import { emit } from "../../../core/state.js";
import { getCommerce7Config } from "../../../core/configAccessors.js";
import { loadStripeJs, createCardElements, tokenizeCard } from "../../../features/optional/stripe_c7.js";

export default class ClubCheckoutForm {
  render(message) {
    const savedCards = Array.isArray(message?.data?.savedCards) ? message.data.savedCards : [];
    const stripeConfigured = !!getCommerce7Config("stripe_publishable_key");

    const form = document.createElement("form");
    form.className = "ws-checkout-form";
    form.noValidate = true;

    const sec = document.createElement("div");
    sec.className = "ws-checkout-section";
    const head = document.createElement("div");
    head.className = "ws-checkout-section-head";
    const title = document.createElement("p");
    title.className = "ws-checkout-section-title";
    title.textContent = "Payment";
    head.appendChild(title);
    sec.appendChild(head);

    const fields = document.createElement("div");
    fields.className = "ws-checkout-fields";
    sec.appendChild(fields);

    let selectedId = null;
    // True once "+ Add new card" is active — the submit handler reads this
    // (rather than re-deriving it) so a saved-card pick before opening the
    // new-card form, or vice versa, is unambiguous.
    let newCardMode = false;
    let stripeCtx = null; // { stripeInstance, cardNumberElement } once mounted
    // Declared here (before the saved-card radios below, which reference them
    // in their "change" handler to collapse back out of new-card mode) —
    // built further down, after the saved-card list.
    let addCardBtn = null;
    let newCardWrap = null;

    if (!savedCards.length) {
      const note = document.createElement("p");
      note.textContent = stripeConfigured
        ? "You don't have a saved payment method yet."
        : "You don't have a saved payment method yet. Please add a card to your account, then come back to finish joining.";
      fields.appendChild(note);
    } else {
      const renderRow = (card, isDefaultCard) => {
        const label = document.createElement("label");
        label.className = "ws-checkout-field";
        label.style.display = "flex";
        label.style.flexDirection = "row";
        label.style.alignItems = "center";
        label.style.gap = "10px";
        label.style.cursor = "pointer";

        const radio = document.createElement("input");
        radio.type = "radio";
        radio.name = "savedCard";
        radio.value = card.id;
        radio.checked = !!isDefaultCard;
        if (isDefaultCard) selectedId = card.id;
        radio.addEventListener("change", () => {
          selectedId = card.id;
          // Picking a saved card after having opened "+ Add new card" backs
          // back out of new-card mode instead of leaving both active.
          if (newCardMode) {
            newCardMode = false;
            if (newCardWrap) newCardWrap.hidden = true;
            if (addCardBtn) addCardBtn.hidden = false;
          }
        });
        label.appendChild(radio);

        const info = document.createElement("div");
        info.style.flex = "1 1 auto";
        const brand = document.createElement("div");
        brand.style.fontWeight = "600";
        brand.textContent = `${card.cardBrand || "Card"}${isDefaultCard ? " (Default)" : ""}`;
        info.appendChild(brand);
        const details = document.createElement("div");
        details.style.fontSize = "12px";
        details.textContent = `${card.maskedCardNumber || ""} · Expiry: ${card.expiryMo}/${card.expiryYr}`;
        info.appendChild(details);
        label.appendChild(info);

        fields.appendChild(label);
      };

      const defaultCard = savedCards.find((c) => c.isDefault) || savedCards[0];
      renderRow(defaultCard, true);
      savedCards.filter((c) => c !== defaultCard).forEach((c) => renderRow(c, false));
    }

    // "+ Add new card" — Stripe-tokenized new card, only offered when this
    // tenant has a publishable key configured (see class doc comment above).
    // One-directional reveal (no collapse-back button), same UX pattern as
    // CheckoutForm.js's "+ Use a new card" — picking a saved card again
    // (handled in renderRow's radio "change" above) is the way back.
    let holderInput = null;
    let holderErr = null;
    let cardError = null;
    if (stripeConfigured) {
      addCardBtn = document.createElement("button");
      addCardBtn.type = "button";
      addCardBtn.className = "ws-checkout-submit ws-club-add-card-btn";
      addCardBtn.style.background = "transparent";
      addCardBtn.style.color = "inherit";
      addCardBtn.style.border = "1px solid #e5e5e5";
      addCardBtn.textContent = "+ Add new card";
      fields.appendChild(addCardBtn);

      newCardWrap = document.createElement("div");
      newCardWrap.className = "ws-checkout-fields ws-club-new-card-fields";
      newCardWrap.hidden = true;
      fields.appendChild(newCardWrap);

      const holderGroup = document.createElement("div");
      holderGroup.className = "ws-checkout-field";
      const holderLabel = document.createElement("label");
      holderLabel.textContent = "Card Holder Name";
      holderGroup.appendChild(holderLabel);
      holderInput = document.createElement("input");
      holderInput.type = "text";
      holderInput.className = "ws-checkout-input";
      holderInput.placeholder = "John Doe";
      holderGroup.appendChild(holderInput);
      holderErr = document.createElement("small");
      holderErr.className = "ws-checkout-error";
      holderGroup.appendChild(holderErr);
      newCardWrap.appendChild(holderGroup);

      const numberGroup = document.createElement("div");
      numberGroup.className = "ws-checkout-field";
      const numberLabel = document.createElement("label");
      numberLabel.textContent = "Card Number";
      numberGroup.appendChild(numberLabel);
      const numberEl = document.createElement("div");
      numberEl.className = "ws-checkout-input ws-club-stripe-el";
      numberGroup.appendChild(numberEl);
      newCardWrap.appendChild(numberGroup);

      const row = document.createElement("div");
      row.className = "ws-checkout-row ws-checkout-row--2";
      const expiryGroup = document.createElement("div");
      expiryGroup.className = "ws-checkout-field";
      const expiryLabel = document.createElement("label");
      expiryLabel.textContent = "Expiry";
      expiryGroup.appendChild(expiryLabel);
      const expiryEl = document.createElement("div");
      expiryEl.className = "ws-checkout-input ws-club-stripe-el";
      expiryGroup.appendChild(expiryEl);
      row.appendChild(expiryGroup);

      const cvcGroup = document.createElement("div");
      cvcGroup.className = "ws-checkout-field";
      const cvcLabel = document.createElement("label");
      cvcLabel.textContent = "CVC";
      cvcGroup.appendChild(cvcLabel);
      const cvcEl = document.createElement("div");
      cvcEl.className = "ws-checkout-input ws-club-stripe-el";
      cvcGroup.appendChild(cvcEl);
      row.appendChild(cvcGroup);
      newCardWrap.appendChild(row);

      cardError = document.createElement("small");
      cardError.className = "ws-checkout-error ws-checkout-submit-error";
      newCardWrap.appendChild(cardError);

      addCardBtn.addEventListener("click", async () => {
        newCardMode = true;
        selectedId = null;
        fields.querySelectorAll('input[type="radio"]').forEach((r) => (r.checked = false));
        addCardBtn.hidden = true;
        newCardWrap.hidden = false;
        submit.disabled = false;
        if (!stripeCtx) {
          try {
            await loadStripeJs();
            stripeCtx = createCardElements({ numberEl, expiryEl, cvcEl });
          } catch {
            cardError.textContent = "Payment form failed to load. Please try again.";
          }
        }
      });
    }

    form.appendChild(sec);

    const footer = document.createElement("div");
    footer.className = "ws-checkout-footer";
    const submit = document.createElement("button");
    submit.type = "submit";
    submit.className = "ws-checkout-submit";
    submit.textContent = "Place Order";
    submit.disabled = !savedCards.length;
    footer.appendChild(submit);
    form.appendChild(footer);

    form.addEventListener("submit", async (e) => {
      e.preventDefault();

      if (newCardMode) {
        const cardHolderName = holderInput.value.trim();
        holderErr.textContent = "";
        cardError.textContent = "";
        if (!cardHolderName) {
          holderErr.textContent = "This field is required";
          return;
        }
        if (!stripeCtx) {
          cardError.textContent = "Payment form isn't ready yet. Please try again.";
          return;
        }
        submit.disabled = true;
        const { token, error } = await tokenizeCard(
          stripeCtx.stripeInstance,
          stripeCtx.cardNumberElement,
          cardHolderName,
        );
        if (error || !token) {
          cardError.textContent = error?.message || "Card tokenization failed. Please try again.";
          submit.disabled = false;
          return;
        }
        const brand = token.card.brand
          ? token.card.brand.charAt(0).toUpperCase() + token.card.brand.slice(1)
          : "Card";
        emit("ws-club-payment-submit", {
          card: {
            tokenOnFile: token.id,
            maskedCardNumber: `************${token.card.last4}`,
            cardBrand: brand,
            cardHolderName: token.card.name,
            expiryMo: token.card.exp_month,
            expiryYr: token.card.exp_year,
          },
        });
        return;
      }

      if (!selectedId) return;
      submit.disabled = true;
      emit("ws-club-payment-submit", { cardId: selectedId });
    });

    return form;
  }
}
